<template>
  <div class="invoice-stats">
    <header class="pb-4 pt-6 sm:pb-6">
      <div class="flex flex-wrap items-center gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
        <h1 class="text-base font-semibold leading-7">Cashflow</h1>
        <RadioGroup v-model="statsDateRange" class="order-last flex w-full gap-x-8 text-sm font-semibold leading-6 sm:order-none sm:w-auto sm:border-l sm:border-slate-300 sm:pl-6 sm:leading-7">
          <RadioGroupLabel class="sr-only">Date Range Filter</RadioGroupLabel>
          <RadioGroupOption v-slot="{ checked }" :value="dateRangeConstants.last_30_days" class="cursor-pointer">
            <span :class="checked ? 'text-accent' : 'text-base-300'">Last 30 days</span>
          </RadioGroupOption>

          <RadioGroupOption v-slot="{ checked }" :value="dateRangeConstants.this_month" class="cursor-pointer">
            <span :class="checked ? 'text-accent' : 'text-base-300'">This month</span>
          </RadioGroupOption>

          <RadioGroupOption v-slot="{ checked }" :value="dateRangeConstants.this_year" class="cursor-pointer">
            <span :class="checked ? 'text-accent' : 'text-base-300'">YTD</span>
          </RadioGroupOption>
        </RadioGroup>
      </div>
    </header>
    <AppStats :items="invoiceStats" />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, Ref } from 'vue'
import AppStats, { StatItem } from '@/components/app/AppStats.vue'
import InvoiceService from '@/services/InvoiceService'
import { INVOICE_STATUS } from '@/models/invoice'
import { useQuery } from '@tanstack/vue-query'
import { dateRangeConstants, dateRangeFilter, orderByDesc } from '@/utils/queryFilters'
import { currency } from '@/utils'
import {
  RadioGroup,
  RadioGroupLabel,
  RadioGroupOption,
} from '@headlessui/vue'

const statsDateRange = ref(dateRangeConstants.last_30_days)

const invoiceService = new InvoiceService()
const { data: invoices } = useQuery({
  queryKey: ['invoices',  statsDateRange],
  queryFn: async () => {
    const data = await invoiceService.where(
      [dateRangeFilter('invoiceDate', statsDateRange.value)],
      [orderByDesc('invoiceDate')]
    )

    return data
  }
})

// Total revenue; sum of all invoices with status 'paid'
const totalRevenue = computed(() => {
  if (!invoices.value) return '--'
  const paidInvoices = invoices.value?.filter(invoice => invoice.status === INVOICE_STATUS.PAID)
  const total = paidInvoices.reduce((acc, invoice) => acc + invoice.total, 0)
  return total == 0 ? '--' : currency(total)
})

// Total outstanding invoices
const totalOutstandingInvoices = computed(() => {
  if (!invoices.value) return '--'
  const outstandingInvoices = invoices.value?.filter(invoice => invoice.status === INVOICE_STATUS.OUTSTANDING)
  const total = outstandingInvoices.reduce((acc, invoice) => acc + invoice.balance, 0)
  return total == 0 ? '--' : currency(total)
})

// Total overdue invoices
const totalOverdueInvoices = computed(() => {
  if (!invoices.value) return '--'
  const overdueInvoices = invoices.value?.filter(invoice => invoice.status === INVOICE_STATUS.OVERDUE)
  const total = overdueInvoices.reduce((acc, invoice) => acc + invoice.balance, 0)
  return total == 0 ? '--' : currency(total)
})

const invoiceStats: Ref<StatItem[]> = ref([
  { label: 'totalRevenue', name: 'Total Revenue', value: totalRevenue, change: '+4.75%', changeType: 'positive' },
  { label: 'overdueInvoices', name: 'Overdue invoices', value: totalOverdueInvoices, change: '+54.02%', changeType: 'negative' },
  { label: 'outstandingInvoices', name: 'Outstanding invoices', value: totalOutstandingInvoices, change: '-1.39%', changeType: 'positive' },
  // todo: add avg time to pay
])
</script>